// tokenRefresh.ts — single-flight refresh-token helper (race fix 2026-07-08).
//
// The backend refresh token is SINGLE-USE (auth_service.refresh_access_token
// revokes the old token on every call). If two concurrent callers each POST
// /auth/refresh with the same stored token, the second one 401s and the old
// code wiped localStorage -> intermittent logout on cold loads of /tech.
//
// This module guarantees at most ONE in-flight /auth/refresh per page.
// Both AuthContext (fetch-based) and services/api.ts (axios interceptor)
// share it, so they can never double-consume the token.

export interface RefreshResult {
  ok: boolean;
  accessToken: string | null;
  // true  = the server DEFINITIVELY rejected the refresh token (401/403,
  //         or we had none) -> the session is dead, safe to clear storage.
  // false = transient failure (network error / 5xx) -> KEEP tokens; a later
  //         attempt or the next page load can still succeed.
  definitive: boolean;
}

let inFlight: Promise<RefreshResult> | null = null;

async function doRefresh(): Promise<RefreshResult> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    return { ok: false, accessToken: null, definitive: true };
  }
  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (res.ok) {
      const data = await res.json();
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      return { ok: true, accessToken: data.access_token, definitive: false };
    }
    // 401/403 = token revoked/expired server-side. Anything else (5xx,
    // gateway errors) is transient and must NOT kill the session.
    return {
      ok: false,
      accessToken: null,
      definitive: res.status === 401 || res.status === 403,
    };
  } catch {
    return { ok: false, accessToken: null, definitive: false };
  }
}

// All concurrent callers share the same promise; once it settles the slot
// re-opens so a FUTURE refresh (with the newly stored token) can run.
export function refreshTokensSingleFlight(): Promise<RefreshResult> {
  if (!inFlight) {
    inFlight = doRefresh().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}
